import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import svg from "../../utils/svg"
import { formatCurrency } from "../../utils/utils"
import styles from "./PerimeterPaywall.module.scss"

// ---------------------------------------------------------------------------
// "Perimeter" (`/perimeter`) — a self-contained FEATURE-SPECIFIC paywall for the
// real Premium **Location Based Lock** feature ("only allow unlocking the app in
// approved GPS locations"). No prior paywall has heroed it.
//
// The whole screen IS the feature, dramatised as a dusk RADAR / GEOFENCE SCOPE:
// a warm "Home" beacon at the centre of a top-down scope, your memories resting
// as photo markers around it, and a glowing blue SAFE-ZONE ring you can resize
// by dragging its handle. A "you" marker sits inside the ring. Drag yourself
// past the boundary and the whole vault SEALS itself — every marker latches shut
// and a "VAULT SEALED" plate drops — because that is exactly what Location Lock
// does: the moment you leave the places you trust, the vault stops opening. Walk
// back inside and it re-opens. A slow radar sweep keeps watch.
//
// Divergence: untaken feature (Location Lock), untaken mechanic (geofence
// boundary-cross / drag-a-radius), untaken value frame (place-based ambient
// protection), guardian/sentinel register. Renders on built-in debug data;
// photos reuse bundled public/byheart/*.jpg (no egress). Yearly is featured.
// ---------------------------------------------------------------------------

const PUB = process.env.PUBLIC_URL ?? ""
const photo = (file: string) => `${PUB}/byheart/${file}.jpg`

const VB = 340 // scope viewBox size
const CX = 170
const CY = 170
const PM_R = 17 // photo-marker radius
const R_MIN = 66
const R_MAX = 150

type PlanKey = "yearly" | "monthly"

type Marker = { img: string; x: number; y: number }

// Your memories, clustered at home — all comfortably inside the default zone.
const MARKERS: Marker[] = [
  { img: "m02", x: CX - 6, y: CY - 45 },
  { img: "m05", x: CX + 41, y: CY - 15 },
  { img: "m08", x: CX + 31, y: CY + 35 },
  { img: "m11", x: CX - 9, y: CY + 47 },
  { img: "m14", x: CX - 45, y: CY + 19 },
  { img: "m17", x: CX - 41, y: CY - 25 },
]

const YOU_HOME = { x: CX, y: CY + 84 } // inside, resting below the cluster
const YOU_AWAY = { x: CX + 104, y: CY + 78 } // outside the default ring (demo)

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))
const dist = (ax: number, ay: number, bx: number, by: number) =>
  Math.hypot(ax - bx, ay - by)

function PerimeterPaywall() {
  const { state, actions } = useAppState()
  const reduce = useReducedMotion()

  // Match the safe-area insets to the dusk scope so the screen reads as one
  // dark instrument top-to-bottom.
  useEffect(() => {
    actions.setHeaderInsetColor("#0e1730")
    actions.setFooterInsetColor("#080e1c")
  }, [actions])

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : ""
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : ""
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : ""
  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 0
    return Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
  }, [yearlyProduct, monthlyProduct])
  const trialDays = yearlyProduct?.introPricing?.period ?? 0

  // ── scope state ───────────────────────────────────────────────────────────
  const [radius, setRadius] = useState(102)
  const [you, setYou] = useState(YOU_HOME)
  const [drag, setDrag] = useState<null | "you" | "radius">(null)
  const [smooth, setSmooth] = useState(false) // eased transitions during auto-demo
  const [touched, setTouched] = useState(false) // user has grabbed something
  const [plan, setPlan] = useState<PlanKey>("yearly")

  const svgRef = useRef<SVGSVGElement>(null)
  const dragRef = useRef<null | "you" | "radius">(null)
  const timers = useRef<number[]>([])

  const sealed = dist(you.x, you.y, CX, CY) > radius
  const meters = Math.round((radius * 3.4) / 10) * 10

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
  }
  const endDemo = useCallback(() => {
    clearTimers()
    setSmooth(false)
  }, [])

  // Teach the gesture once: walk out (seal) → walk back (open) → hand it over.
  useEffect(() => {
    if (reduce) return
    setSmooth(true)
    const t1 = window.setTimeout(() => setYou(YOU_AWAY), 900)
    const t2 = window.setTimeout(() => setYou(YOU_HOME), 2500)
    const t3 = window.setTimeout(() => setSmooth(false), 3300)
    timers.current.push(t1, t2, t3)
    return clearTimers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toLocal = (e: React.PointerEvent) => {
    const el = svgRef.current
    if (!el) return { x: CX, y: CY }
    const r = el.getBoundingClientRect()
    return {
      x: ((e.clientX - r.left) / r.width) * VB,
      y: ((e.clientY - r.top) / r.height) * VB,
    }
  }

  const start = (which: "you" | "radius") => (e: React.PointerEvent) => {
    e.preventDefault()
    dragRef.current = which
    setDrag(which)
    setTouched(true)
    endDemo()
    try {
      svgRef.current?.setPointerCapture(e.pointerId)
    } catch {}
  }

  const onMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return
    const p = toLocal(e)
    if (dragRef.current === "you") {
      setYou({ x: clamp(p.x, 14, VB - 14), y: clamp(p.y, 14, VB - 14) })
    } else {
      setRadius(clamp(dist(p.x, p.y, CX, CY), R_MIN, R_MAX))
    }
  }

  const onUp = (e: React.PointerEvent) => {
    dragRef.current = null
    setDrag(null)
    try {
      svgRef.current?.releasePointerCapture(e.pointerId)
    } catch {}
  }

  // radius handle sits on the ring, up-and-to-the-right
  const hAng = -Math.PI / 4
  const hx = CX + radius * Math.cos(hAng)
  const hy = CY + radius * Math.sin(hAng)

  const youMove = smooth && !drag ? styles.youEased : ""
  const ringMove = drag === "radius" ? "" : styles.ringEased

  const handleClose = () => native.postNativeEvent("close")
  const buy = () => {
    const p = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (p) native.subscribe(p)
  }

  const SecurityPlus = svg.featureIcons.SecurityPlus
  const CloudVault = svg.featureIcons.CloudVault
  const Wifi = svg.featureIcons.Wifi

  return (
    <div className={styles.page}>
      {/* chrome ------------------------------------------------------------- */}
      <header className={styles.top}>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <span className={styles.brand}>
          Private Photo Vault <b>Premium</b>
        </span>
        <span className={styles.badge}>LOCATION LOCK</span>
      </header>

      {/* live status readout ------------------------------------------------ */}
      <div className={`${styles.status} ${sealed ? styles.statusSealed : ""}`}>
        <span className={styles.dot} />
        <span className={styles.statusText}>
          {sealed ? "Outside your safe zone" : "Inside your safe zone"}
        </span>
        <span className={styles.statusState}>
          {sealed ? "Vault sealed" : "Vault open"}
        </span>
      </div>

      {/* the scope ---------------------------------------------------------- */}
      <div className={styles.scopeWrap}>
        <svg
          ref={svgRef}
          className={styles.scope}
          viewBox={`0 0 ${VB} ${VB}`}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerLeave={onUp}
          onPointerCancel={onUp}
        >
          <defs>
            <radialGradient id="pmScopeBg" cx="50%" cy="42%" r="70%">
              <stop offset="0%" stopColor="#1b2c52" />
              <stop offset="55%" stopColor="#141f3c" />
              <stop offset="100%" stopColor="#0c1428" />
            </radialGradient>
            <radialGradient id="pmZone" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#3f7bff" stopOpacity="0.28" />
              <stop offset="70%" stopColor="#3f7bff" stopOpacity="0.10" />
              <stop offset="100%" stopColor="#3f7bff" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="pmZoneSealed" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#ffa23e" stopOpacity="0.24" />
              <stop offset="75%" stopColor="#ffa23e" stopOpacity="0.08" />
              <stop offset="100%" stopColor="#ffa23e" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="pmSweep" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#7fa8ff" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#7fa8ff" stopOpacity="0" />
            </linearGradient>
            <radialGradient id="pmHome" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#ffd59a" />
              <stop offset="60%" stopColor="#ffb15a" />
              <stop offset="100%" stopColor="#f7913a" />
            </radialGradient>
            {MARKERS.map((m, i) => (
              <clipPath id={`pm${i}`} key={i}>
                <circle cx={m.x} cy={m.y} r={PM_R} />
              </clipPath>
            ))}
          </defs>

          {/* scope face */}
          <circle cx={CX} cy={CY} r={166} fill="url(#pmScopeBg)" />

          {/* range rings + bearings */}
          {[52, 92, 132, 164].map((r) => (
            <circle
              key={r}
              cx={CX}
              cy={CY}
              r={r}
              className={styles.rangeRing}
            />
          ))}
          <line x1={CX} y1={8} x2={CX} y2={332} className={styles.grat} />
          <line x1={8} y1={CY} x2={332} y2={CY} className={styles.grat} />
          {[
            ["N", CX, 20],
            ["E", 324, CY + 4],
            ["S", CX, 328],
            ["W", 16, CY + 4],
          ].map(([t, x, y]) => (
            <text
              key={t as string}
              x={x as number}
              y={y as number}
              className={styles.bearing}
              textAnchor="middle"
            >
              {t}
            </text>
          ))}

          {/* radar sweep */}
          {!reduce && (
            <g
              className={styles.sweep}
              style={{ transformOrigin: `${CX}px ${CY}px` }}
            >
              <path
                d={`M${CX} ${CY} L${CX} ${CY - 164} A164 164 0 0 1 ${
                  CX + 164 * Math.sin(1.05)
                } ${CY - 164 * Math.cos(1.05)} Z`}
                fill="url(#pmSweep)"
              />
            </g>
          )}

          {/* the safe zone */}
          <circle
            cx={CX}
            cy={CY}
            r={radius}
            fill={sealed ? "url(#pmZoneSealed)" : "url(#pmZone)"}
            className={ringMove}
          />
          <circle
            cx={CX}
            cy={CY}
            r={radius}
            className={`${styles.fence} ${sealed ? styles.fenceSealed : ""} ${ringMove}`}
          />

          {/* zone label riding the ring */}
          <g
            className={ringMove}
            style={{ transform: `translate(${hx}px, ${hy}px)` }}
          >
            <text
              className={styles.zoneLabel}
              x={0}
              y={-23}
              textAnchor="middle"
            >
              SAFE ZONE
            </text>
            <text
              className={styles.zoneMeters}
              x={0}
              y={-12}
              textAnchor="middle"
            >
              {meters} m
            </text>
          </g>

          {/* photo markers — your memories at home */}
          {MARKERS.map((m, i) => (
            <g
              key={m.img}
              className={`${styles.marker} ${sealed ? styles.markerSealed : ""}`}
            >
              <circle
                cx={m.x}
                cy={m.y}
                r={PM_R + 3}
                className={styles.markerHalo}
              />
              <image
                href={photo(m.img)}
                x={m.x - PM_R}
                y={m.y - PM_R}
                width={PM_R * 2}
                height={PM_R * 2}
                clipPath={`url(#pm${i})`}
                preserveAspectRatio="xMidYMid slice"
              />
              <circle
                cx={m.x}
                cy={m.y}
                r={PM_R}
                className={styles.markerRing}
              />
              {/* padlock that latches shut on seal */}
              <g
                className={styles.markerLock}
                style={{ transform: `translate(${m.x}px, ${m.y}px)` }}
              >
                <circle r={8.5} className={styles.lockPad} />
                <path
                  d="M-3 -0.6a3 3 0 0 1 6 0 M-4 -0.6h8v6h-8z"
                  className={styles.lockGlyph}
                  transform="translate(0 0.4)"
                />
              </g>
            </g>
          ))}

          {/* home beacon */}
          <g>
            <circle cx={CX} cy={CY} r={24} className={styles.homePulse} />
            <circle
              cx={CX}
              cy={CY}
              r={14}
              fill="url(#pmHome)"
              className={styles.homeCore}
            />
            <path
              d={`M${CX - 6} ${CY + 1} L${CX} ${CY - 6} L${CX + 6} ${CY + 1} M${
                CX - 4.5
              } ${CY - 0.2} V${CY + 5} H${CX + 4.5} V${CY - 0.2}`}
              className={styles.homeGlyph}
            />
          </g>

          {/* radius handle */}
          <g
            style={{ transform: `translate(${hx}px, ${hy}px)` }}
            className={ringMove}
          >
            <circle
              r={17}
              className={styles.handleHit}
              onPointerDown={start("radius")}
            />
            <circle
              r={8}
              className={`${styles.handle} ${sealed ? styles.handleSealed : ""}`}
            />
            <circle r={3} className={styles.handleDot} />
          </g>

          {/* YOU marker */}
          <g
            className={youMove}
            style={{ transform: `translate(${you.x}px, ${you.y}px)` }}
          >
            <circle
              r={22}
              className={styles.youHit}
              onPointerDown={start("you")}
            />
            {!sealed && <circle r={16} className={styles.youAura} />}
            <circle
              r={9}
              className={`${styles.youCore} ${sealed ? styles.youCoreSealed : ""}`}
            />
            <circle r={3.2} className={styles.youPin} />
          </g>
        </svg>

        {/* haze + seal plate (crisp HTML over the scope) */}
        <div className={`${styles.haze} ${sealed ? styles.hazeOn : ""}`} />
        <div
          className={`${styles.sealPlate} ${sealed ? styles.sealPlateOn : ""}`}
        >
          <span className={styles.sealLock}>
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path
                d="M7 10V8a5 5 0 0 1 10 0v2"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <rect
                x="5"
                y="10"
                width="14"
                height="9"
                rx="2.4"
                fill="currentColor"
              />
            </svg>
          </span>
          <b>Vault sealed</b>
          <span>You stepped outside your safe zone</span>
        </div>

        {/* first-run hint */}
        {!touched && (
          <div className={`${styles.hint} ${sealed ? styles.hintHide : ""}`}>
            Drag yourself past the ring
          </div>
        )}
      </div>

      {/* safe places — plural, real feature approves multiple GPS locations */}
      <div className={styles.places}>
        <button className={`${styles.place} ${styles.placeOn}`}>
          <span className={styles.placeIcon}>⌂</span> Home
        </button>
        <button className={styles.place}>
          <span className={styles.placeIcon}>✦</span> The studio
        </button>
        <button className={`${styles.place} ${styles.placeAdd}`}>
          + Add a place
        </button>
      </div>

      {/* the pitch ---------------------------------------------------------- */}
      <section className={styles.pitch}>
        <h1>
          Your vault knows <em>where home is.</em>
        </h1>
        <p>
          Location Lock keeps your photos openable only in the places you trust.
          Step outside your safe zone and the vault seals itself —
          automatically, every single time. No tapping lock. No second thought.
        </p>
      </section>

      {/* quietly included with Premium ------------------------------------- */}
      <div className={styles.also}>
        <span className={styles.alsoLabel}>Every memory inside stays</span>
        <div className={styles.alsoRow}>
          <span className={styles.alsoItem}>
            <SecurityPlus className={styles.alsoGlyph} /> Location-locked
          </span>
          <span className={styles.alsoItem}>
            <CloudVault className={styles.alsoGlyph} /> Encrypted &amp; backed
            up
          </span>
          <span className={styles.alsoItem}>
            <Wifi className={styles.alsoGlyph} /> In sync everywhere
          </span>
        </div>
      </div>

      {/* the offer ---------------------------------------------------------- */}
      <div className={styles.offer}>
        <button
          className={`${styles.planYear} ${plan === "yearly" ? styles.planOn : ""}`}
          onClick={() => setPlan("yearly")}
        >
          <div className={styles.planYearHead}>
            <span className={styles.planName}>Yearly</span>
            {savings > 0 && (
              <span className={styles.save}>SAVE {savings}%</span>
            )}
          </div>
          <div className={styles.planYearPrice}>
            <b>{yearlyPrice}</b>
            <span className={styles.per}>/year</span>
          </div>
          <div className={styles.planYearNote}>
            just {yearlyPerMonth}/mo · billed once a year
          </div>
        </button>

        <button
          className={`${styles.planMonth} ${plan === "monthly" ? styles.planOn : ""}`}
          onClick={() => setPlan("monthly")}
        >
          <span className={styles.planName}>Monthly</span>
          <span className={styles.planMonthPrice}>
            {monthlyPrice}
            <span className={styles.per}>/mo</span>
          </span>
        </button>

        <button className={styles.cta} onClick={buy}>
          Set your safe zone
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `${trialDays ? `${trialDays}-day free trial, then ` : ""}${yearlyPrice}/yr`
              : `${monthlyPrice}/mo`}
          </span>
        </button>

        <p className={styles.reassure}>
          Nothing is ever deleted — Location Lock only decides where your vault
          can be opened. Cancel anytime.
        </p>
        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default PerimeterPaywall
